import debounce from 'lodash.debounce';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const INJECTION_DELAY = 20;
const DEFAULT_TIMEOUT = 4800;
const DEFAULT_RETRY_COUNT = 3;
const BASE_ICONS_URL = "https://core.amo.tools/icons/"

export interface LoadOptions {
  flushImmediate?: boolean;
  timeout?: number;
  retryCount?: number;
}

interface RequestParams extends RequestInit, LoadOptions {}



class Loader {
  private fragment: DocumentFragment | null = null;

  private symbolsMountingPoint: SVGElement | null = null;

  private cache = new Set();

  private debouncedFlushSvg = debounce(this.flushSvg, INJECTION_DELAY);

  public getId(name: string): string {

    const SVG_NAME_REGEXP = /([0-9a-zA-z\-_.]+)\.svg$/i;
    const matched = (name.match(BASE_ICONS_URL)
      ? name
      : this.buildUrl(name)).match(SVG_NAME_REGEXP);

    if (!matched) {
      throw new Error('svg url does not match pattern');
    }

    return matched[1] as string;
  }

  public async load(
    name: string,
    {flushImmediate, timeout, retryCount}: LoadOptions = {}
  ) {
    const url = this.buildUrl(name)

    if (this.cache.has(url)) {
      return;
    }

    this.cache.add(url);

    return this.fetchSvg(url, {timeout, retryCount})
      .then((svgString) => {
        this.accumulateSvg({url, svgString})
        flushImmediate ? this.flushSvg() : this.debouncedFlushSvg()
      })
      .catch((error: Error) => {
        this.cache.delete(url);
        throw error;
      })
  }

  private buildUrl(name: string) {
    return BASE_ICONS_URL + name + ".svg"
  }

  private getSymbolMountPoint() {
    if (!this.symbolsMountingPoint) {
      const sprite = document.createElementNS(SVG_NAMESPACE, 'svg');
      this.symbolsMountingPoint = sprite;

      sprite.id = "svg-root"
      sprite.ariaHidden = 'true';
      sprite.style.width = '0';
      sprite.style.height = '0';
      sprite.style.position = 'absolute';
      sprite.style.overflow = 'hidden';
      sprite.style.zIndex = '-99999';

      document.body.appendChild(sprite);
    }

    return this.symbolsMountingPoint;
  }

  private flushSvg() {
    const sprite = this.getSymbolMountPoint();

    if (this.fragment) {
      sprite.appendChild(this.fragment);
      this.fragment = null;
    }
  }

  private parseSvgString(svg: string) {
    return new DOMParser().parseFromString(svg, 'image/svg+xml').children[0];
  }

  private accumulateSvg({url, svgString}: { url: string, svgString: string }) {
    const id = this.getId(url);
    const symbol = this.svgToSymbol({id, svgString});

    this.fragment = this.fragment || document.createDocumentFragment();
    this.fragment.append(symbol);
  }

  private svgToSymbol({id, svgString}: { id: string; svgString: string }): SVGElement {
    const svgDocument = this.parseSvgString(svgString);
    const viewBox = svgDocument.getAttribute('viewBox');

    const symbol = document.createElementNS(SVG_NAMESPACE, 'symbol');

    if (viewBox) {
      symbol.setAttributeNS(null, 'viewBox', viewBox);
    }

    symbol.setAttributeNS(null, 'id', id);
    symbol.innerHTML = svgDocument.innerHTML;

    return symbol;
  }

  private async fetchWithTimeout(url: string, params?: RequestParams) {
    const timeout = params?.timeout || DEFAULT_TIMEOUT;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    return fetch(url, {
      ...params,
      signal: controller.signal
    })
      .then((response) => response)
      .catch((error) => {
        throw error
      })
      .finally(() => clearTimeout(timeoutId))
  }

  private fetchSvg(url: string, params?: RequestParams) {

    let retryCount = params?.retryCount === undefined ? DEFAULT_RETRY_COUNT : params?.retryCount;

    const doFetch = (url: string, params?: RequestParams): Promise<string> => {
      return this.fetchWithTimeout(url, params)
        .then((response) => {
          if (response.ok) {
            return response.text();
          }

          throw new Error(`Unable to load SVG file: ${url}`);
        })
        .catch((error) => {
          if (retryCount > 0) {
            retryCount--;
            return doFetch(url, params);
          }

          throw error;
        })
    }

    return doFetch(url, params)
      .then((response) => {
        return response;
      })
  }
}

export const loader = new Loader();

